import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { getApiErrorStatus, isApiError } from "./api/utils";
import Sentry from "./integrations/Sentry";

/**
 * An `ApiError` was already triaged by the axios interceptor; a query or
 * mutation opts out of capture entirely with `meta: { sentry: false }`.
 */
export function shouldCaptureCacheError(
  error: unknown,
  meta: Record<string, unknown> | undefined,
): boolean {
  return meta?.sentry !== false && !isApiError(error);
}

/**
 * A 4xx won't change on retry. orgAPI rejects with an `ApiError` (its
 * interceptor rewraps the axios error), so both shapes carry the status.
 */
export function shouldRetryQuery(failureCount: number, error: unknown) {
  const status = getApiErrorStatus(error);
  if (typeof status === "number" && status >= 400 && status < 500) {
    return false;
  }
  return failureCount < 3;
}

/**
 * Must be >= the persist layer's `maxAge`. TanStack Query discards restored
 * entries older than `gcTime` on hydrate, so a lower value silently truncates
 * persistence (default is only 5 minutes).
 *
 * The same bound applies to every *per-query* `gcTime` override, not just this
 * default: the persister stays subscribed to the query cache for the whole
 * session and rewrites the snapshot on every cache event, so a query that
 * garbage-collects earlier than `maxAge` is dropped from localStorage the
 * moment it goes unused for its own `gcTime`. A query that genuinely wants a
 * short `gcTime` must opt out of persistence with `meta: { persist: false }`
 * instead.
 */
export const QUERY_CACHE_GC_TIME_MS = 1000 * 60 * 60 * 24; // 24 hours

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 10,
      gcTime: QUERY_CACHE_GC_TIME_MS,
      refetchOnWindowFocus: true,
      retry: shouldRetryQuery,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!shouldCaptureCacheError(error, query.meta)) return;
      Sentry.captureException(error, {
        extra: {
          queryKey: query.queryKey,
        },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      if (!shouldCaptureCacheError(error, mutation.meta)) return;
      Sentry.captureException(error, {
        extra: {
          mutationKey: mutation.options.mutationKey,
          variables,
          context,
        },
      });
    },
  }),
});

queryClient.setQueryDefaults(["permission"], { gcTime: Infinity });

export { queryClient };
